import React, { useMemo } from 'react';
import { Link } from '@/components/LocalizedLink';
import { Target, Heart, Eye, Award, ArrowRight, FileText, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { useApiData } from '@/hooks/useApiData';

const NO_ITEMS = [];
const NO_PHOTOS = {};
const STATIC_CERT_IDS = ['fssai', 'iec', 'gst'];
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export default function AboutPage() {
  const { data: certificates } = useApiData('/api/certificates', NO_ITEMS);
  const { data: sitePhotos } = useApiData('/api/site-photos', NO_PHOTOS);
  const extraCertificates = useMemo(() => certificates.filter(c => !STATIC_CERT_IDS.includes(c.id)), [certificates]);

  const storyImg = sitePhotos.about_story?.url || '/api/files/asjk-laxmi/uploads/c1aebeae32a2.webp';

  return (
    <div data-testid="about-page">

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-saffron-50/30" />
        <div className="relative max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">About Us</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-earth-dark mt-3 tracking-tight">
              Our Story of Spice & Trust
            </h1>
            <p className="mt-6 text-lg text-earth/60 font-sans leading-relaxed">
              From the heartlands of Maharashtra to global markets &mdash; ASJK Laxmi Global is built on the foundation of quality, traceability, and unwavering commitment to our buyers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <img src={storyImg} alt="ASJK Laxmi Global spice warehouse in Chhatrapati Sambhajinagar Maharashtra" width={1264} height={848} className="w-full rounded-lg shadow-card-lift" loading="lazy" />
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ delay: 0.2 }}>
            <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">Our Journey</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-earth-dark mt-3 tracking-tight">
              From Local Farms to Global Tables
            </h2>
            <div className="mt-6 space-y-4 text-earth/70 font-sans leading-relaxed">
              <p>
                ASJK Laxmi Global was founded with a simple mission: to bring the purest Indian spice powders to the world. Based in Chhatrapati Sambhajinagar (Aurangabad), Maharashtra, we have grown from a small trading house into a trusted export partner for buyers across 15+ countries.
              </p>
              <p>
                Our team combines deep agricultural knowledge with modern export logistics. We work directly with farming communities across Maharashtra, Andhra Pradesh, Rajasthan, and Gujarat to source the finest turmeric, red chillies, coriander, garlic, cumin and black pepper, which we supply as finely ground, export-grade powders.
              </p>
              <p>
                Every batch that leaves our facility is lab-tested, FSSAI-certified, and packaged to meet international import standards. Our buyers trust us because we deliver consistency, transparency, and competitive pricing &mdash; shipment after shipment.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission / Vision / Values */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-saffron-50/30" data-testid="values-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">What Drives Us</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-earth-dark mt-3 tracking-tight">Mission, Vision & Values</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Target, title: 'Our Mission', desc: 'To supply the finest export-grade Indian spice powders with complete farm-to-port traceability, ensuring every buyer receives consistent quality at competitive pricing.' },
              { icon: Eye, title: 'Our Vision', desc: 'To become the most trusted Indian spice export brand globally, recognized for quality, reliability, and sustainable sourcing from Indian farming communities.' },
              { icon: Heart, title: 'Our Values', desc: 'Quality without compromise. Transparent pricing. Respect for farming traditions. Long-term buyer relationships built on trust and dependable delivery.' },
            ].map((item, i) => (
              <motion.div key={item.title} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ delay: i * 0.15 }} className="bg-white p-8 rounded-lg shadow-card-lift border border-saffron-100/50" data-testid={`value-card-${i}`}>
                <div className="w-12 h-12 rounded-lg bg-saffron/10 flex items-center justify-center mb-5">
                  <item.icon size={22} className="text-saffron" />
                </div>
                <h3 className="font-display text-xl font-bold text-earth-dark">{item.title}</h3>
                <p className="mt-3 text-sm text-earth/60 font-sans leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Licenses */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="certifications-section">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron font-sans font-semibold">Verified & Licensed</span>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-earth-dark mt-3 tracking-tight">Certifications & Compliance</h2>
            <p className="mt-4 text-earth/60 font-sans max-w-xl mx-auto">Every export meets stringent food safety and quality benchmarks. All licenses and certifications are up to date.</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Static cert cards with actual numbers */}
            {[
              { name: 'FSSAI License', number: '10018022007595', desc: 'Food Safety and Standards Authority of India', icon: Award },
              { name: 'Import Export Code (IEC)', number: 'CNUPK2717H', desc: 'Directorate General of Foreign Trade, Govt. of India', icon: Award },
              { name: 'GST Registration', number: '27CNUPK2717H1ZM', desc: 'Goods and Services Tax, Maharashtra', icon: Award },
            ].map((cert, i) => (
              <motion.div
                key={cert.name}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} transition={{ delay: i * 0.1 }}
                className="bg-white p-6 rounded-lg border border-saffron-100/50 shadow-card-lift text-center"
                data-testid={`cert-card-${i}`}
              >
                <div className="w-14 h-14 rounded-full bg-saffron/10 flex items-center justify-center mx-auto mb-4">
                  <cert.icon size={24} className="text-saffron" />
                </div>
                <h3 className="font-display text-lg font-bold text-earth-dark">{cert.name}</h3>
                <p className="font-sans text-saffron font-semibold text-sm mt-1" data-testid={`cert-number-${i}`}>{cert.number}</p>
                <p className="mt-2 text-xs text-earth/50 font-sans">{cert.desc}</p>
              </motion.div>
            ))}

            {/* Dynamic certs from API */}
            {extraCertificates.map((cert, i) => (
              <motion.div key={cert.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ delay: (i + 3) * 0.1 }} className="bg-white p-6 rounded-lg border border-saffron-100/50 shadow-card-lift text-center">
                <div className="w-14 h-14 rounded-full bg-saffron/10 flex items-center justify-center mx-auto mb-4">
                  <FileText size={24} className="text-saffron" />
                </div>
                <h3 className="font-display text-lg font-bold text-earth-dark">{cert.name}</h3>
                {cert.number && <p className="font-sans text-saffron font-semibold text-sm mt-1">{cert.number}</p>}
                {cert.file_url && (
                  <a href={cert.file_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-saffron/70 mt-2 hover:text-saffron transition-colors font-sans">
                    <ExternalLink size={10} /> View Document
                  </a>
                )}
              </motion.div>
            ))}
          </div>

          {/* Additional compliance badges */}
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            {['APEDA Registered', 'Lab Tested', 'Export Licensed', 'Phytosanitary Compliant'].map(badge => (
              <span key={badge} className="text-[10px] uppercase tracking-wider text-saffron/70 border border-saffron/20 px-4 py-2 rounded-full font-sans bg-saffron/5">
                {badge}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-saffron text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">Ready to Partner with Us?</h2>
          <p className="mt-2 text-saffron-100 font-sans">Get in touch and let's discuss how we can supply your spice requirements.</p>
          <Link to="/contact" className="mt-6 inline-flex items-center gap-2 bg-white text-saffron-dark font-sans font-semibold text-sm px-8 py-3.5 rounded-md hover:bg-cream-warm transition-colors" data-testid="about-cta">
            Contact Us <ArrowRight size={16} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
